import { useState, useEffect } from 'react';
import { db, storage } from '../lib/firebase';
import {
    collection, getDocs, doc, addDoc, updateDoc, deleteDoc,
    Timestamp, query, orderBy
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';

interface SignedFile {
    playerId: string;
    playerName: string;
    fileUrl: string;
    fileName: string;
    uploadedAt: Timestamp;
}

interface PermissionSlip {
    id: string;
    title: string;
    description: string;
    content: string;
    status: 'draft' | 'active' | 'archived';
    eventDate?: Timestamp;
    location?: string;
    departureTime?: string;
    returnTime?: string;
    whatToBring?: string;
    createdAt: Timestamp;
    updatedAt: Timestamp;
    createdBy: string;
    signedFiles: SignedFile[];
}

type FilterTab = 'all' | 'active' | 'archived' | 'draft';

interface FormData {
    title: string;
    description: string;
    content: string;
    status: 'draft' | 'active' | 'archived';
    eventDate: Date | undefined;
    location: string;
    departureTime: string;
    returnTime: string;
    whatToBring: string;
}

const emptySlip: FormData = {
    title: '',
    description: '',
    content: '',
    status: 'draft',
    eventDate: undefined,
    location: '',
    departureTime: '',
    returnTime: '',
    whatToBring: '',
};

export default function AdminPermissionSlipsPage() {
    const { user, userDoc } = useAuth();

    const [slips, setSlips] = useState<PermissionSlip[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<FilterTab>('all');

    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [editingSlip, setEditingSlip] = useState<PermissionSlip | null>(null);
    const [formData, setFormData] = useState<FormData>(emptySlip);
    const [saving, setSaving] = useState(false);

    // Upload modal
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadSlip, setUploadSlip] = useState<PermissionSlip | null>(null);
    const [uploadPlayerName, setUploadPlayerName] = useState('');
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    // Delete confirmation
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

    // Load permission slips
    useEffect(() => {
        async function loadSlips() {
            try {
                const q = query(collection(db, 'permissionSlips'), orderBy('createdAt', 'desc'));
                const snap = await getDocs(q);
                const data = snap.docs.map(d => {
                    const docData = d.data();
                    return {
                        id: d.id,
                        title: docData.title || '',
                        description: docData.description || '',
                        content: docData.content || '',
                        status: docData.status || 'draft',
                        eventDate: docData.eventDate,
                        location: docData.location || '',
                        departureTime: docData.departureTime || '',
                        returnTime: docData.returnTime || '',
                        whatToBring: docData.whatToBring || '',
                        createdAt: docData.createdAt,
                        updatedAt: docData.updatedAt,
                        createdBy: docData.createdBy || '',
                        signedFiles: docData.signedFiles || [],
                    } as PermissionSlip;
                });
                setSlips(data);
            } catch (err) {
                console.error('Error loading permission slips:', err);
            } finally {
                setLoading(false);
            }
        }
        if (user && (userDoc?.role === 'admin' || userDoc?.role === 'coach')) {
            loadSlips();
        }
    }, [user, userDoc]);

    const filteredSlips = slips.filter(s => {
        if (filter === 'all') return true;
        return s.status === filter;
    });

    const openCreateModal = () => {
        setEditingSlip(null);
        setFormData(emptySlip);
        setShowModal(true);
    };

    const openEditModal = (slip: PermissionSlip) => {
        setEditingSlip(slip);
        setFormData({
            title: slip.title,
            description: slip.description,
            content: slip.content,
            status: slip.status,
            eventDate: slip.eventDate ? slip.eventDate.toDate() : undefined,
            location: slip.location || '',
            departureTime: slip.departureTime || '',
            returnTime: slip.returnTime || '',
            whatToBring: slip.whatToBring || '',
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingSlip(null);
        setFormData(emptySlip);
    };

    const handleSave = async () => {
        if (!formData.title.trim()) return;
        setSaving(true);

        try {
            const slipData = {
                title: formData.title.trim(),
                description: formData.description.trim(),
                content: formData.content.trim(),
                status: formData.status,
                eventDate: formData.eventDate ? Timestamp.fromDate(formData.eventDate) : undefined,
                location: formData.location.trim(),
                departureTime: formData.departureTime.trim(),
                returnTime: formData.returnTime.trim(),
                whatToBring: formData.whatToBring.trim(),
                updatedAt: Timestamp.now(),
            };

            if (editingSlip) {
                await updateDoc(doc(db, 'permissionSlips', editingSlip.id), slipData);
                setSlips(slips.map(s =>
                    s.id === editingSlip.id ? { ...s, ...slipData } : s
                ));
            } else {
                const newDoc = await addDoc(collection(db, 'permissionSlips'), {
                    ...slipData,
                    createdAt: Timestamp.now(),
                    createdBy: user?.uid,
                    signedFiles: [],
                });
                const newSlip: PermissionSlip = {
                    id: newDoc.id,
                    title: slipData.title,
                    description: slipData.description,
                    content: slipData.content,
                    status: slipData.status,
                    eventDate: slipData.eventDate || undefined,
                    location: slipData.location,
                    departureTime: slipData.departureTime,
                    returnTime: slipData.returnTime,
                    whatToBring: slipData.whatToBring,
                    createdAt: Timestamp.now(),
                    updatedAt: slipData.updatedAt,
                    createdBy: user?.uid || '',
                    signedFiles: [],
                };
                setSlips([newSlip, ...slips]);
            }
            closeModal();
        } catch (err) {
            console.error('Error saving permission slip:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleArchive = async (slip: PermissionSlip) => {
        const newStatus = slip.status === 'archived' ? 'active' : 'archived';
        try {
            await updateDoc(doc(db, 'permissionSlips', slip.id), {
                status: newStatus,
                updatedAt: Timestamp.now(),
            });
            setSlips(slips.map(s =>
                s.id === slip.id ? { ...s, status: newStatus } : s
            ));
        } catch (err) {
            console.error('Error archiving slip:', err);
        }
    };

    const handleDelete = async (slipId: string) => {
        try {
            await deleteDoc(doc(db, 'permissionSlips', slipId));
            setSlips(slips.filter(s => s.id !== slipId));
            setDeleteConfirm(null);
        } catch (err) {
            console.error('Error deleting slip:', err);
        }
    };

    const openUploadModal = (slip: PermissionSlip) => {
        setUploadSlip(slip);
        setUploadPlayerName('');
        setUploadFile(null);
        setShowUploadModal(true);
    };

    const handleUpload = async () => {
        if (!uploadSlip || !uploadFile || !uploadPlayerName.trim()) return;
        setUploading(true);

        try {
            const fileName = `${Date.now()}_${uploadFile.name}`;
            const storageRef = ref(storage, `permissionSlips/${uploadSlip.id}/${fileName}`);
            await uploadBytes(storageRef, uploadFile);
            const fileUrl = await getDownloadURL(storageRef);

            const newSignedFile: SignedFile = {
                playerId: uploadPlayerName.trim().toLowerCase().replace(/\s+/g, '_'),
                playerName: uploadPlayerName.trim(),
                fileUrl,
                fileName,
                uploadedAt: Timestamp.now(),
            };

            const updatedFiles = [...(uploadSlip.signedFiles || []), newSignedFile];
            await updateDoc(doc(db, 'permissionSlips', uploadSlip.id), {
                signedFiles: updatedFiles,
                updatedAt: Timestamp.now(),
            });

            setSlips(slips.map(s =>
                s.id === uploadSlip.id ? { ...s, signedFiles: updatedFiles } : s
            ));
            setShowUploadModal(false);
        } catch (err) {
            console.error('Error uploading file:', err);
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteFile = async (slip: PermissionSlip, file: SignedFile) => {
        try {
            const storageRef = ref(storage, `permissionSlips/${slip.id}/${file.fileName}`);
            await deleteObject(storageRef);

            const updatedFiles = slip.signedFiles.filter(f => f.fileName !== file.fileName);
            await updateDoc(doc(db, 'permissionSlips', slip.id), {
                signedFiles: updatedFiles,
                updatedAt: Timestamp.now(),
            });

            setSlips(slips.map(s =>
                s.id === slip.id ? { ...s, signedFiles: updatedFiles } : s
            ));
        } catch (err) {
            console.error('Error deleting file:', err);
        }
    };

    const handlePrint = (slip: PermissionSlip) => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        const eventDateStr = slip.eventDate
            ? slip.eventDate.toDate().toLocaleDateString('en-US', {
                timeZone: 'UTC',
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            })
            : '';

        const contentWithBreaks = slip.content.replace(/\n/g, '<br>');

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>${slip.title} - Permission Slip</title>
                <style>
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    body {
                        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                        line-height: 1.5;
                        padding: 0.5in;
                        max-width: 8.5in;
                        margin: 0 auto;
                        color: #000;
                        background: #fff;
                        font-size: 11pt;
                    }
                    .header {
                        text-align: center;
                        margin-bottom: 20px;
                        padding-bottom: 12px;
                        border-bottom: 3px solid #c00;
                    }
                    .school-name {
                        font-size: 16pt;
                        font-weight: bold;
                        color: #c00;
                        margin-bottom: 4px;
                    }
                    .subtitle {
                        font-size: 12pt;
                        color: #333;
                    }
                    .section {
                        margin-bottom: 16px;
                    }
                    .section-header {
                        background: #333;
                        color: white;
                        padding: 6px 12px;
                        font-size: 10pt;
                        font-weight: bold;
                        margin-bottom: 10px;
                    }
                    .section-content {
                        padding: 0 8px;
                    }
                    .title {
                        font-size: 14pt;
                        font-weight: bold;
                        text-align: center;
                        margin: 16px 0 8px 0;
                    }
                    .event-info {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 6px 16px;
                        margin-bottom: 12px;
                    }
                    .event-info-item {
                        display: flex;
                        gap: 8px;
                    }
                    .event-info-item label {
                        font-weight: bold;
                        min-width: 80px;
                    }
                    .content {
                        margin-bottom: 16px;
                    }
                    .content p {
                        margin-bottom: 8px;
                    }
                    .form-row {
                        display: flex;
                        gap: 16px;
                        margin-bottom: 12px;
                    }
                    .form-field {
                        flex: 1;
                    }
                    .form-field.small {
                        flex: 0 0 120px;
                    }
                    .form-field label {
                        display: block;
                        font-weight: bold;
                        font-size: 9pt;
                        margin-bottom: 4px;
                    }
                    .form-field .line {
                        border-bottom: 1px solid #333;
                        height: 22px;
                    }
                    .checkbox-section {
                        margin: 16px 0;
                        padding: 12px;
                        border: 1px solid #ccc;
                        background: #f9f9f9;
                    }
                    .checkbox-item {
                        display: flex;
                        align-items: flex-start;
                        gap: 8px;
                        margin-bottom: 6px;
                    }
                    .checkbox {
                        width: 14px;
                        height: 14px;
                        border: 1px solid #333;
                        flex-shrink: 0;
                        margin-top: 2px;
                    }
                    .medical-section {
                        margin: 16px 0;
                        padding: 12px;
                        border: 1px solid #ffc107;
                        background: #fff3cd;
                    }
                    .medical-section h4 {
                        color: #856404;
                        margin-bottom: 8px;
                        font-size: 10pt;
                    }
                    .medical-box {
                        border: 1px solid #333;
                        min-height: 60px;
                        background: #fff;
                        padding: 8px;
                    }
                    .footer {
                        margin-top: 20px;
                        padding-top: 12px;
                        border-top: 1px solid #ccc;
                        font-size: 9pt;
                        color: #666;
                        text-align: center;
                    }
                    @media print {
                        body {
                            padding: 0;
                        }
                        .no-print {
                            display: none;
                        }
                    }
                    .print-btn {
                        position: fixed;
                        top: 20px;
                        right: 20px;
                        padding: 12px 24px;
                        background: #c00;
                        color: white;
                        border: none;
                        border-radius: 8px;
                        cursor: pointer;
                        font-size: 14px;
                        font-weight: bold;
                    }
                    .print-btn:hover {
                        background: #a00;
                    }
                </style>
            </head>
            <body>
                <button class="print-btn no-print" onclick="window.print()">🖨️ Print</button>
                
                <div class="header">
                    <div class="school-name">🏴‍☠️ Peck Junior High/High School</div>
                    <div class="subtitle">Permission to Ride Form</div>
                </div>
                
                <h1 class="title">${slip.title}</h1>
                
                <div class="section">
                    <div class="section-header">EVENT INFORMATION</div>
                    <div class="section-content">
                        <div class="event-info">
                            <div class="event-info-item"><label>Event:</label> ${slip.title}</div>
                            ${eventDateStr ? `<div class="event-info-item"><label>Date:</label> ${eventDateStr}</div>` : ''}
                            ${slip.location ? `<div class="event-info-item"><label>Location:</label> ${slip.location}</div>` : ''}
                            ${slip.departureTime ? `<div class="event-info-item"><label>Departure:</label> ${slip.departureTime}</div>` : ''}
                            ${slip.returnTime ? `<div class="event-info-item"><label>Return:</label> ${slip.returnTime}</div>` : ''}
                        </div>
                        ${slip.description ? `<p><strong>Details:</strong> ${slip.description}</p>` : ''}
                        ${slip.whatToBring ? `<p><strong>What to Bring:</strong> ${slip.whatToBring}</p>` : ''}
                        <div class="content">
                            ${contentWithBreaks}
                        </div>
                    </div>
                </div>
                
                <div class="section">
                    <div class="section-header">STUDENT INFORMATION</div>
                    <div class="section-content">
                        <div class="form-row">
                            <div class="form-field">
                                <label>Student Name:</label>
                                <div class="line"></div>
                            </div>
                            <div class="form-field small">
                                <label>Grade:</label>
                                <div class="line"></div>
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-field">
                                <label>Student Emergency Contact Number:</label>
                                <div class="line"></div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="checkbox-section">
                    <strong>Permission to Ride:</strong>
                    <p style="margin: 8px 0;">I, the undersigned, grant permission for my child, listed above, to attend the event detailed above. I understand that my child will be transported to and from the event in a school vehicle under the supervision of Coach Brian Anklam.</p>
                    <div class="checkbox-item">
                        <div class="checkbox"></div>
                        <span>I give permission for my child to participate in this event.</span>
                    </div>
                    <div class="checkbox-item">
                        <div class="checkbox"></div>
                        <span>I give permission for my child to travel via school-approved transportation.</span>
                    </div>
                    <div class="checkbox-item">
                        <div class="checkbox"></div>
                        <span>I understand my child is responsible for following all Peck Community Schools and esports program policies.</span>
                    </div>
                    <div class="checkbox-item">
                        <div class="checkbox"></div>
                        <span>In case of emergency, I authorize Coach Anklam to seek medical treatment for my child if I cannot be reached.</span>
                    </div>
                </div>
                
                <div class="section">
                    <div class="section-header">PARENT/GUARDIAN INFORMATION</div>
                    <div class="section-content">
                        <div class="form-row">
                            <div class="form-field">
                                <label>Parent/Guardian Name (Print):</label>
                                <div class="line"></div>
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-field">
                                <label>Parent/Guardian Signature:</label>
                                <div class="line"></div>
                            </div>
                            <div class="form-field small">
                                <label>Date:</label>
                                <div class="line"></div>
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-field">
                                <label>Emergency Contact Number:</label>
                                <div class="line"></div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="medical-section">
                    <h4>⚕️ MEDICAL INFORMATION</h4>
                    <p style="margin-bottom: 8px; font-size: 10pt;">Please list any medical conditions or allergies that we should be aware of:</p>
                    <div class="medical-box"></div>
                </div>
                
                <div class="footer">
                    If you have any questions or concerns, please feel free to contact the school office. Thank you for your support!
                    <br><br>
                    <strong>Brian Anklam</strong> | eSports Director<br>
                    anklamb@peckschools.net | Call/Text (810) 378-0645
                </div>
                
                <script>
                    // Auto-scale content to fit one page
                    (function() {
                        const PAGE_HEIGHT = 10.5 * 96; // 10.5 inches in pixels (leaving 0.5in margins)
                        const MIN_SCALE = 0.65; // Don't go below 65% size
                        let scale = 1.0;
                        
                        function getContentHeight() {
                            return document.body.scrollHeight;
                        }
                        
                        function applyScale(s) {
                            document.body.style.fontSize = (11 * s) + 'pt';
                            document.querySelectorAll('.school-name').forEach(el => el.style.fontSize = (16 * s) + 'pt');
                            document.querySelectorAll('.subtitle').forEach(el => el.style.fontSize = (12 * s) + 'pt');
                            document.querySelectorAll('.title').forEach(el => el.style.fontSize = (14 * s) + 'pt');
                            document.querySelectorAll('.section-header').forEach(el => el.style.fontSize = (10 * s) + 'pt');
                            document.querySelectorAll('.section-header').forEach(el => el.style.padding = (6 * s) + 'px ' + (12 * s) + 'px');
                            document.querySelectorAll('.section').forEach(el => el.style.marginBottom = (14 * s) + 'px');
                            document.querySelectorAll('.form-row').forEach(el => el.style.marginBottom = (10 * s) + 'px');
                            document.querySelectorAll('.form-field .line').forEach(el => el.style.height = (20 * s) + 'px');
                            document.querySelectorAll('.checkbox-section').forEach(el => { 
                                el.style.padding = (10 * s) + 'px';
                                el.style.margin = (12 * s) + 'px 0';
                            });
                            document.querySelectorAll('.checkbox-item').forEach(el => el.style.marginBottom = (5 * s) + 'px');
                            document.querySelectorAll('.medical-section').forEach(el => el.style.padding = (10 * s) + 'px');
                            document.querySelectorAll('.medical-box').forEach(el => el.style.minHeight = (50 * s) + 'px');
                            document.querySelectorAll('.header').forEach(el => { 
                                el.style.marginBottom = (16 * s) + 'px';
                                el.style.paddingBottom = (10 * s) + 'px';
                            });
                            document.querySelectorAll('.footer').forEach(el => {
                                el.style.marginTop = (16 * s) + 'px';
                                el.style.paddingTop = (10 * s) + 'px';
                                el.style.fontSize = (9 * s) + 'pt';
                            });
                            document.body.style.lineHeight = (1.4 + (0.1 * s)).toString();
                        }
                        
                        // Wait for rendering
                        setTimeout(function() {
                            while (getContentHeight() > PAGE_HEIGHT && scale > MIN_SCALE) {
                                scale -= 0.02;
                                applyScale(scale);
                            }
                            
                            if (scale < 1.0) {
                                console.log('Auto-scaled to ' + Math.round(scale * 100) + '% to fit one page');
                            }
                        }, 100);
                    })();
                </script>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    const formatDate = (timestamp: Timestamp | undefined) => {
        if (!timestamp) return '—';
        return timestamp.toDate().toLocaleDateString();
    };

    const getStatusBadge = (status: string) => {
        const styles: Record<string, string> = {
            draft: 'bg-yellow-500/20 text-yellow-400',
            active: 'bg-green-500/20 text-green-400',
            archived: 'bg-secondary-600 text-secondary-400',
        };
        return styles[status] || styles.draft;
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-8 max-w-5xl">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-display font-bold text-white">Permission Slips</h1>
                    <p className="text-secondary-400">{slips.length} permission slips</p>
                </div>
                <button onClick={openCreateModal} className="btn-primary">
                    + Create New
                </button>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2 mb-6">
                {(['all', 'active', 'draft', 'archived'] as const).map(f => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f
                            ? 'bg-primary-500 text-white'
                            : 'bg-secondary-800 text-secondary-300 hover:bg-secondary-700'
                            }`}
                    >
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                ))}
            </div>

            {/* Slips Table */}
            <div className="card overflow-x-auto">
                {filteredSlips.length === 0 ? (
                    <div className="text-center py-12 text-secondary-400">
                        <p className="text-lg mb-2">No permission slips found</p>
                        <p className="text-sm">Click "Create New" to add your first permission slip.</p>
                    </div>
                ) : (
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-secondary-700">
                                <th className="text-left py-3 px-3 text-secondary-400 font-medium">Title</th>
                                <th className="text-left py-3 px-3 text-secondary-400 font-medium">Description</th>
                                <th className="text-left py-3 px-3 text-secondary-400 font-medium">Created</th>
                                <th className="text-center py-3 px-3 text-secondary-400 font-medium">Status</th>
                                <th className="text-center py-3 px-3 text-secondary-400 font-medium">Signed</th>
                                <th className="text-right py-3 px-3 text-secondary-400 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSlips.map(slip => (
                                <tr key={slip.id} className="border-b border-secondary-800 hover:bg-secondary-800/50">
                                    <td className="py-3 px-3">
                                        <span className="font-medium text-white">{slip.title}</span>
                                    </td>
                                    <td className="py-3 px-3 text-secondary-300 max-w-xs truncate">
                                        {slip.description || '—'}
                                    </td>
                                    <td className="py-3 px-3 text-secondary-400">
                                        {formatDate(slip.createdAt)}
                                    </td>
                                    <td className="py-3 px-3 text-center">
                                        <span className={`text-xs px-2 py-0.5 rounded ${getStatusBadge(slip.status)}`}>
                                            {slip.status}
                                        </span>
                                    </td>
                                    <td className="py-3 px-3 text-center">
                                        <span className="text-secondary-300">
                                            {slip.signedFiles?.length || 0}
                                        </span>
                                    </td>
                                    <td className="py-3 px-3">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => handlePrint(slip)}
                                                className="px-2 py-1 text-xs bg-purple-500/20 hover:bg-purple-500/30 rounded text-purple-400"
                                            >
                                                Print
                                            </button>
                                            <button
                                                onClick={() => openEditModal(slip)}
                                                className="px-2 py-1 text-xs bg-secondary-700 hover:bg-secondary-600 rounded text-secondary-300"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => openUploadModal(slip)}
                                                className="px-2 py-1 text-xs bg-blue-500/20 hover:bg-blue-500/30 rounded text-blue-400"
                                            >
                                                Upload
                                            </button>
                                            <button
                                                onClick={() => handleArchive(slip)}
                                                className="px-2 py-1 text-xs bg-secondary-700 hover:bg-secondary-600 rounded text-secondary-300"
                                            >
                                                {slip.status === 'archived' ? 'Restore' : 'Archive'}
                                            </button>
                                            {deleteConfirm === slip.id ? (
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={() => handleDelete(slip.id)}
                                                        className="px-2 py-1 text-xs bg-red-500 hover:bg-red-600 rounded text-white"
                                                    >
                                                        Confirm
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirm(null)}
                                                        className="px-2 py-1 text-xs bg-secondary-700 hover:bg-secondary-600 rounded text-secondary-300"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => setDeleteConfirm(slip.id)}
                                                    className="px-2 py-1 text-xs bg-red-500/20 hover:bg-red-500/30 rounded text-red-400"
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Signed Files Section */}
            {filteredSlips.some(s => s.signedFiles?.length > 0) && (
                <div className="mt-8">
                    <h2 className="text-xl font-display font-bold text-white mb-4">Uploaded Signed Slips</h2>
                    {filteredSlips.filter(s => s.signedFiles?.length > 0).map(slip => (
                        <div key={slip.id} className="card mb-4">
                            <h3 className="text-lg font-medium text-white mb-3">{slip.title}</h3>
                            <div className="flex flex-wrap gap-2">
                                {slip.signedFiles.map((file, idx) => (
                                    <div key={idx} className="flex items-center gap-2 bg-secondary-800 px-3 py-2 rounded-lg">
                                        <a
                                            href={file.fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-400 hover:text-blue-300"
                                        >
                                            📄 {file.playerName}
                                        </a>
                                        <button
                                            onClick={() => handleDeleteFile(slip, file)}
                                            className="text-red-400 hover:text-red-300 text-sm"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                    <div className="bg-secondary-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-display font-bold text-white mb-6">
                            {editingSlip ? 'Edit Permission Slip' : 'Create Permission Slip'}
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="label">Title *</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                                    className="input"
                                    placeholder="e.g., Fall 2026 Season Registration"
                                />
                            </div>

                            <div>
                                <label className="label">Description</label>
                                <input
                                    type="text"
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    className="input"
                                    placeholder="Brief description of the purpose"
                                />
                            </div>

                            <div>
                                <label className="label">Event Date (Optional)</label>
                                <input
                                    type="date"
                                    value={formData.eventDate ? formData.eventDate.toISOString().split('T')[0] : ''}
                                    onChange={e => setFormData({
                                        ...formData,
                                        eventDate: e.target.value ? new Date(e.target.value) : undefined
                                    })}
                                    className="input"
                                />
                            </div>

                            <div>
                                <label className="label">Location</label>
                                <input
                                    type="text"
                                    value={formData.location}
                                    onChange={e => setFormData({ ...formData, location: e.target.value })}
                                    className="input"
                                    placeholder="e.g., Kettering University, Flint, MI"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label">Departure Time</label>
                                    <input
                                        type="text"
                                        value={formData.departureTime}
                                        onChange={e => setFormData({ ...formData, departureTime: e.target.value })}
                                        className="input"
                                        placeholder="e.g., 7:30 AM from Peck High School"
                                    />
                                </div>
                                <div>
                                    <label className="label">Expected Return Time</label>
                                    <input
                                        type="text"
                                        value={formData.returnTime}
                                        onChange={e => setFormData({ ...formData, returnTime: e.target.value })}
                                        className="input"
                                        placeholder="e.g., Approximately 6:00 PM"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="label">What to Bring</label>
                                <textarea
                                    value={formData.whatToBring}
                                    onChange={e => setFormData({ ...formData, whatToBring: e.target.value })}
                                    className="input min-h-[80px]"
                                    placeholder="e.g., Lunch money, water bottle, personal controller (if preferred)"
                                />
                            </div>

                            <div>
                                <label className="label">Status</label>
                                <select
                                    value={formData.status}
                                    onChange={e => setFormData({ ...formData, status: e.target.value as 'draft' | 'active' | 'archived' })}
                                    className="input"
                                >
                                    <option value="draft">Draft</option>
                                    <option value="active">Active</option>
                                    <option value="archived">Archived</option>
                                </select>
                            </div>

                            <div>
                                <label className="label">Permission Slip Content/Terms</label>
                                <textarea
                                    value={formData.content}
                                    onChange={e => setFormData({ ...formData, content: e.target.value })}
                                    className="input min-h-[200px]"
                                    placeholder="Enter the full permission slip text, terms and conditions, liability waivers, etc."
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button onClick={closeModal} className="btn-secondary">
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving || !formData.title.trim()}
                                className="btn-primary disabled:opacity-50"
                            >
                                {saving ? 'Saving...' : (editingSlip ? 'Save Changes' : 'Create Slip')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Modal */}
            {showUploadModal && uploadSlip && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                    <div className="bg-secondary-800 rounded-xl p-6 w-full max-w-md">
                        <h2 className="text-xl font-display font-bold text-white mb-6">
                            Upload Signed Slip
                        </h2>
                        <p className="text-secondary-400 text-sm mb-4">
                            Uploading for: <span className="text-white">{uploadSlip.title}</span>
                        </p>

                        <div className="space-y-4">
                            <div>
                                <label className="label">Player Name *</label>
                                <input
                                    type="text"
                                    value={uploadPlayerName}
                                    onChange={e => setUploadPlayerName(e.target.value)}
                                    className="input"
                                    placeholder="Enter player name"
                                />
                            </div>

                            <div>
                                <label className="label">Signed Permission Slip (PDF/Image) *</label>
                                <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={e => setUploadFile(e.target.files?.[0] || null)}
                                    className="input file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary-500 file:text-white file:cursor-pointer"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button onClick={() => setShowUploadModal(false)} className="btn-secondary">
                                Cancel
                            </button>
                            <button
                                onClick={handleUpload}
                                disabled={uploading || !uploadPlayerName.trim() || !uploadFile}
                                className="btn-primary disabled:opacity-50"
                            >
                                {uploading ? 'Uploading...' : 'Upload'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
